<script setup lang="ts">
import { useId } from "vue";

// Optional props accept an explicit `undefined`: PixTextField passes its own optional props
// straight through, and consumers that type-check with `exactOptionalPropertyTypes` reject
// `string | undefined` for a plain `string?`. Vue treats a passed `undefined` as absent, so the
// defaults below still apply.
type HPixTextFieldProps = {
  modelValue?: string | undefined;
  id?: string | undefined;
  type?: "text" | "email" | "password" | "tel" | "url" | "number";
  placeholder?: string | undefined;
  disabled?: boolean;
  required?: boolean;
  name?: string | undefined;
  autocomplete?: string | undefined;
  min?: number | undefined;
  max?: number | undefined;
  /** The native `maxlength`: the browser stops typing and pasting past this many characters. */
  maxlength?: number | undefined;
};

type HPixTextFieldEmits = {
  "update:modelValue": [value: string];
  blur: [];
};

const props = withDefaults(defineProps<HPixTextFieldProps>(), {
  modelValue: "",
  type: "text",
  disabled: false,
  required: false,
});

const emits = defineEmits<HPixTextFieldEmits>();

const generatedId = useId();
const inputId = () => props.id ?? generatedId;

const handleInput = (event: Event) => {
  const target = event.target as HTMLInputElement;
  emits("update:modelValue", target.value);
};
</script>

<template>
  <input
    :id="inputId()"
    class="h-pix-text-field"
    :type="props.type"
    :value="props.modelValue"
    :placeholder="props.placeholder"
    :disabled="props.disabled"
    :aria-disabled="props.disabled || undefined"
    :required="props.required || undefined"
    :name="props.name"
    :autocomplete="props.autocomplete"
    :min="props.min"
    :max="props.max"
    :maxlength="props.maxlength"
    @input="handleInput"
    @blur="emits('blur')"
  />
</template>
